/**
 * Batch 3.4 (2026-02-XX) — runtime kill-switch for the A3 apply flow.
 *
 * Reads the flow from `GET /api/public/apply-flow-config` on mount, then
 * layers two client-side overrides on top:
 *
 *   - URL query `?_a3=1`      — one-shot override for a test device
 *   - localStorage `bmi_a3_override` — sticky override that survives
 *                                       reloads (set once, reuse across
 *                                       the whole apply flow)
 *
 * Both overrides force the flow to `email_first` regardless of server
 * config, so a SINGLE real phone can walk the entire A3 path on prod
 * BEFORE ops flips the global flag for everyone else.
 *
 * The hook is intentionally small + failure-tolerant: if the config
 * endpoint 500s, we fall back to `"register"` — the pre-A3 known-working
 * flow. Nobody's apply flow silently breaks because of a config fetch
 * hiccup.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";

const LOCAL_STORAGE_KEY = "bmi_a3_override";
const URL_QUERY_PARAM = "_a3";
const DEFAULT_FLOW = "register";
const TARGET_FLOW = "email_first";

let _cachedFlow = null;   // process-wide cache — one fetch per SPA lifetime
let _cachedAt = 0;
const CACHE_TTL_MS = 60_000;   // 60s — long enough to avoid a fetch storm,
                               // short enough that an ops flip goes live
                               // within a minute for freshly loaded pages.

function _readOverride() {
    try {
        if (typeof window === "undefined") return false;
        const url = new URLSearchParams(window.location.search);
        if (url.has(URL_QUERY_PARAM) && url.get(URL_QUERY_PARAM) !== "0") {
            // Sticky — persist so downstream pages inherit the override.
            try { localStorage.setItem(LOCAL_STORAGE_KEY, "1"); } catch { /* private mode */ }
            return true;
        }
        if (localStorage.getItem(LOCAL_STORAGE_KEY) === "1") return true;
    } catch { /* SSR or private-mode — treat as no override */ }
    return false;
}

async function _fetchFlowFromServer() {
    // Serve from cache if fresh.
    if (_cachedFlow && (Date.now() - _cachedAt) < CACHE_TTL_MS) {
        return _cachedFlow;
    }
    try {
        const { data } = await api.get("/public/apply-flow-config", { timeout: 5000 });
        _cachedFlow = data?.apply_flow || DEFAULT_FLOW;
        _cachedAt = Date.now();
        return _cachedFlow;
    } catch {
        // Config fetch failed — fall back to the pre-A3 flow so nobody
        // is stranded. Do NOT throw; the whole point of this hook is
        // to be a safety net.
        return DEFAULT_FLOW;
    }
}

/**
 * Returns `{ applyFlow, loading, isEmailFirst, isRegister }`.
 * Never throws; always resolves to a valid flow. Default until the
 * first fetch completes is the SAFE flow (`register`), so a slow
 * config fetch never accidentally routes candidates into A3.
 */
export function useApplyFlow() {
    // Overrides are synchronous and known upfront — apply them
    // immediately without waiting for the server fetch.
    const overridden = _readOverride();
    const [applyFlow, setApplyFlow] = useState(overridden ? TARGET_FLOW : DEFAULT_FLOW);
    const [loading, setLoading] = useState(!overridden);

    useEffect(() => {
        if (overridden) return;   // no fetch needed
        let cancelled = false;
        (async () => {
            const flow = await _fetchFlowFromServer();
            if (!cancelled) {
                setApplyFlow(flow);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [overridden]);

    return {
        applyFlow,
        loading,
        isEmailFirst: applyFlow === TARGET_FLOW,
        isRegister: applyFlow === DEFAULT_FLOW,
        overridden,
    };
}

/**
 * Utility for `CandidateApplyProtectedRoute` — checks whether the browser
 * holds a valid A3 sessionStorage token for ANY active application. If
 * yes, we can treat this browser as an authenticated A3 candidate for
 * downstream apply routes even though there's no full candidate session.
 */
export function hasA3ApplicationToken() {
    try {
        if (typeof window === "undefined") return false;
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const key = sessionStorage.key(i);
            if (!key || !key.startsWith("bmi-apply-entry-")) continue;
            const raw = sessionStorage.getItem(key);
            if (!raw) continue;
            try {
                const parsed = JSON.parse(raw);
                if (parsed?.candidate_token) return true;
            } catch { /* bad JSON — skip */ }
        }
    } catch { /* private-mode / SSR */ }
    return false;
}
